import { Dimensions } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { DocumentType } from "@/constants/Documents";
import { Pdf } from "@/crossPlatform/Pdf";
import { WebView } from "@/crossPlatform/WebView";
import useShare from "@/hooks/useShare";
import useAppMonitor from "@/services/AppMonitor";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import SafeNativeModal from "@/ui/composed/modal/SafeNativeModal";
import { FAIconName, IconButton } from "@/ui/core";
import ModalLayout from "@/ui/layout/ModalLayout";
import { getDocumentTypeFromFileName } from "@/utils/documents.util";
import { isProductionEnvironment } from "@/utils/environment.util";
import { isWeb } from "@/utils/platform.util";

interface AttachmentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  uri: string;
}

const AttachmentPreviewModal = ({
  isOpen,
  onClose,
  title,
  uri,
}: AttachmentPreviewModalProps) => {
  const { shareFile } = useShare();
  const { captureException } = useAppMonitor();
  const { height, width } = Dimensions.get("window");
  const isPdf = getDocumentTypeFromFileName(uri) === DocumentType.PDF;

  const handleSharePress = () => {
    void shareFile({
      fileUrl: uri,
      title,
    });
  };

  const renderAttachmentViewer = () => {
    if (isPdf) {
      return (
        <Pdf
          onError={(error) => {
            captureException(new Error("Error loading PDF", { cause: error }));
          }}
          source={{ uri }}
          style={{ flex: 1, ...(!isWeb() && { height, width }) }}
          trustAllCerts={false}
        />
      );
    }
    return (
      <WebView
        source={{ uri }}
        webviewDebuggingEnabled={!isProductionEnvironment()}
      />
    );
  };

  const modalAccessibilityTitle = "Attachment Preview";

  const renderModalHeader = () => (
    <ModalTitleHeader
      actionComponents={
        <IconButton
          iconName={isWeb() ? FAIconName.Download : FAIconName.ShareFromSquare}
          onPress={handleSharePress}
          testID="handle-share-button"
        />
      }
      modalAccessibilityTitle={modalAccessibilityTitle}
      navigationType="close"
      onDismiss={onClose}
      testID="web-view-modal"
      title={title}
    />
  );

  if (isWeb()) {
    // on web, opening a Pdf opens the file in a new tab, not in an in-app modal; so do not show a modal/modal-header/etc
    if (isPdf) return renderAttachmentViewer();
    return (
      <SafeNativeModal onRequestClose={onClose} transparent visible={isOpen}>
        <ModalLayout
          onBackdropPress={onClose}
          renderHeader={renderModalHeader}
          variant="oversized"
        >
          {renderAttachmentViewer()}
        </ModalLayout>
      </SafeNativeModal>
    );
  }

  return (
    <SafeNativeModal
      animationType="slide"
      onRequestClose={onClose}
      visible={isOpen}
    >
      {renderModalHeader()}
      {renderAttachmentViewer()}
      <SafeAreaView />
    </SafeNativeModal>
  );
};

export default AttachmentPreviewModal;
export type { AttachmentPreviewModalProps };
